import React, { useState, useCallback, useEffect } from 'react';
import { Upload, FileImage, X, Check, Loader2, Settings, Sparkles, Zap } from 'lucide-react';
import apiClient from '../../lib/apiClient';


const ImageUploadManager = () => {
  const [uploads, setUploads] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadedImages, setUploadedImages] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('logos');
  const [faviconUrl, setFaviconUrl] = useState('');
  const [logoUrl, setLogoUrl] = useState('');

  const categories = [
    { id: 'logos', name: 'Logos', icon: Sparkles },
    { id: 'favicons', name: 'Favicons', icon: Settings },
    { id: 'banners', name: 'Banners', icon: FileImage },
    { id: 'icons', name: 'Icons', icon: Zap }
  ];

  // Fetch existing images on mount
  useEffect(() => {
    fetchUploadedImages();
  }, [selectedCategory]);

  const fetchUploadedImages = async () => {
    try {
      const data = await apiClient.get(`/api/storage/images/${selectedCategory}`);
      setUploadedImages(data.files || []);
    } catch (error) {
      console.error('Error fetching images:', error);
    }
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  }, []);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    handleFiles(files);
  };

  const handleFiles = (files) => {
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    const newUploads = imageFiles.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      name: file.name,
      size: file.size,
      preview: URL.createObjectURL(file),
      status: 'pending',
      progress: 0,
      category: selectedCategory
    }));

    setUploads(prev => [...prev, ...newUploads]);
    newUploads.forEach(upload => uploadFile(upload));
  };

  const uploadFile = async (upload) => {
    const formData = new FormData();
    formData.append('image', upload.file);
    formData.append('category', upload.category);

    // Simulated progress (the request has no upload events); capped at 90%
    // until the server responds.
    const progressInterval = setInterval(() => {
      setUploadProgress(prev => ({
        ...prev,
        [upload.id]: Math.min((prev[upload.id] || 0) + 10, 90)
      }));
    }, 200);

    try {
      // apiClient passes FormData through untouched and attaches the auth
      // token — the previous raw fetch sent this upload unauthenticated.
      const result = await apiClient.post('/api/storage/upload', formData);

      setUploadProgress(prev => ({ ...prev, [upload.id]: 100 }));
      setUploads(prev => prev.map(u =>
        u.id === upload.id
          ? { ...u, status: 'success', url: result.url, publicUrl: result.publicUrl }
          : u
      ));

      // If it's a favicon or logo, update the respective state
      if (upload.category === 'favicons') {
        setFaviconUrl(result.publicUrl);
        updateFavicon(result.publicUrl);
      } else if (upload.category === 'logos') {
        setLogoUrl(result.publicUrl);
      }

      // Refresh the uploaded images list
      setTimeout(fetchUploadedImages, 500);
    } catch (error) {
      console.error('Upload error:', error);
      setUploads(prev => prev.map(u =>
        u.id === upload.id ? { ...u, status: 'error' } : u
      ));
    } finally {
      clearInterval(progressInterval);
    }
  };

  const updateFavicon = (url) => {
    // Update favicon in the document
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.getElementsByTagName('head')[0].appendChild(link);
    }
    link.href = url;
  };

  const removeUpload = (id) => {
    setUploads(prev => prev.filter(u => u.id !== id));
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[id];
      return newProgress;
    });
  };

  const setAsActive = async (imageUrl, type) => {
    try {
      await apiClient.post('/api/storage/set-active', { url: imageUrl, type });
      if (type === 'favicon') {
        setFaviconUrl(imageUrl);
        updateFavicon(imageUrl);
      } else if (type === 'logo') {
        setLogoUrl(imageUrl);
      }
    } catch (error) {
      console.error('Error setting active image:', error);
    }
  };

  const selectedCategoryData = categories.find(c => c.id === selectedCategory);
  const Icon = selectedCategoryData?.icon;

  return (
    <div className="image-upload-manager">
      <div className="container">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="page-title">
            Brand Asset Manager
          </h1>
          <p className="page-subtitle">
            Upload and organize your visual identity
          </p>
        </div>

        {/* Category Selector */}
        <div className="category-selector-wrapper">
          <div className="category-selector">
            {categories.map((cat) => {
              const CategoryIcon = cat.icon;
              const isActive = selectedCategory === cat.id;
              
              return (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`btn btn-category ${isActive ? 'active' : ''}`}
                >
                  <CategoryIcon size={18} />
                  <span>{cat.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Upload Area */}
        <div className="mb-12">
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`upload-zone ${dragging ? 'dragging' : ''}`}
          >
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileSelect}
              className="upload-input"
            />
            
            <div className="upload-content">
              <div className={`upload-icon-wrapper ${dragging ? 'dragging' : ''}`}>
                <Upload size={32} className="upload-icon" />
              </div>
              
              <h3 className="upload-title">
                Drop your {selectedCategoryData?.name.toLowerCase()} here
              </h3>
              <p className="upload-description">
                or click to browse • JPG, PNG, GIF, SVG, WebP
              </p>
            </div>
          </div>
        </div>

        {/* Active Uploads */}
        {uploads.length > 0 && (
          <div className="mb-12">
            <h3 className="section-title mb-6">
              Uploading
            </h3>
            <div className="upload-list">
              {uploads.map((upload) => (
                <div
                  key={upload.id}
                  className="upload-item card"
                >
                  <div className="upload-item-content">
                    <div className="upload-preview-wrapper">
                      <img
                        src={upload.preview}
                        alt={upload.name}
                        className="upload-preview"
                      />
                      {upload.status === 'pending' && (
                        <div className="upload-preview-overlay">
                          <Loader2 className="upload-spinner" />
                        </div>
                      )}
                    </div>
                    
                    <div className="upload-info">
                      <p className="upload-filename font-medium">
                        {upload.name}
                      </p>
                      <p className="upload-filesize text-small text-muted">
                        {(upload.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    
                    {upload.status === 'pending' && (
                      <div className="upload-progress-wrapper">
                        <div className="progress">
                          <div
                            className="progress-bar"
                            style={{ width: `${uploadProgress[upload.id] || 0}%` }}
                          />
                        </div>
                      </div>
                    )}
                    
                    {upload.status === 'success' && (
                      <div className="status-badge status-success">
                        <Check size={16} />
                      </div>
                    )}
                    
                    {upload.status === 'error' && (
                      <div className="status-badge status-error">
                        <X size={16} />
                      </div>
                    )}
                    
                    <button
                      onClick={() => removeUpload(upload.id)}
                      className="btn btn-ghost btn-icon btn-sm"
                    >
                      <X size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Uploaded Images Grid */}
        {uploadedImages.length > 0 && (
          <div>
            <h3 className="section-title mb-6">
              {selectedCategoryData?.name} Library
            </h3>
            <div className="image-grid">
              {uploadedImages.map((image, index) => {
                const isActive = (selectedCategory === 'favicons' && image.publicUrl === faviconUrl) ||
                               (selectedCategory === 'logos' && image.publicUrl === logoUrl);
                
                return (
                  <div
                    key={index}
                    className="image-card card-hover"
                  >
                    <div className="image-container">
                      <img
                        src={image.publicUrl || image.url}
                        alt={image.name}
                        className="image-preview"
                      />
                    </div>
                    
                    <div className="image-overlay">
                      <div className="image-overlay-content">
                        <p className="image-name">
                          {image.name}
                        </p>
                        
                        {(selectedCategory === 'favicons' || selectedCategory === 'logos') && (
                          <button
                            onClick={() => setAsActive(image.publicUrl || image.url, selectedCategory === 'favicons' ? 'favicon' : 'logo')}
                            className="btn btn-secondary btn-sm image-action-btn"
                          >
                            Set as {selectedCategory === 'favicons' ? 'Favicon' : 'Logo'}
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {isActive && (
                      <div className="active-indicator">
                        <Check size={14} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageUploadManager;